import { GreenGreen60, OrangeOrange60 } from '@sequencehq/core-components'

import type { CubeEditorData } from 'modules/Cube/domain/cube.domain.types'
import { cubeDeveloperLogGroup } from 'modules/Cube/utils/cubeDeveloperLog'

import type { ShouldSyncQuoteReasons } from './shouldSyncQuote'

/** The badge every one of these lines carries, so the console can be filtered. */
export const QUOTE_SYNC_LOG_LABEL = 'Cube sync'

/**
 * What the editor is holding that the server hasn't been told about, and the
 * comparison that decided it. The two `lastLoadedAt`s are in here because a
 * mismatch means the editor reloaded underneath us, which is the usual
 * explanation for a sync that looks like it came out of nowhere.
 */
export const logQuoteNeedsSync = ({
  reasons,
  previous,
  current,
  previousLastLoadedAt,
  currentLastLoadedAt
}: {
  reasons: ShouldSyncQuoteReasons
  previous: CubeEditorData | undefined
  current: CubeEditorData
  previousLastLoadedAt: Date | undefined
  currentLastLoadedAt: Date | undefined
}) =>
  cubeDeveloperLogGroup({
    label: QUOTE_SYNC_LOG_LABEL,
    summary: summariseNeedsSync(reasons),
    badgeBackgroundColor: OrangeOrange60,
    entries: [
      {
        label: 'Reasons',
        value: () => ({
          cubeDataHasChanged: reasons.cubeDataHasChanged,
          presentationHasChanged: reasons.presentationHasChanged
        })
      },
      {
        label: 'Detailed equality',
        value: reasons.detailedEquality
      },
      {
        label: 'Loaded at',
        value: () => ({
          previous: previousLastLoadedAt,
          current: currentLastLoadedAt
        })
      },
      {
        label: 'Data',
        value: () => ({ previous, current })
      }
    ]
  })

/** The save itself starting, once the debounce has run out. */
export const logQuoteSaveTriggered = ({ reload }: { reload: boolean }) =>
  cubeDeveloperLogGroup({
    label: QUOTE_SYNC_LOG_LABEL,
    summary: summariseSaveTriggered({ reload }),
    badgeBackgroundColor: OrangeOrange60,
    entries: [{ label: 'Save', value: () => ({ reload }) }]
  })

export const logQuoteSyncComplete = ({ elapsedMs }: { elapsedMs: number }) =>
  cubeDeveloperLogGroup({
    label: QUOTE_SYNC_LOG_LABEL,
    summary: summariseSyncComplete({ elapsedMs }),
    badgeBackgroundColor: GreenGreen60,
    entries: [{ label: 'Took', value: () => `${elapsedMs}ms` }]
  })

/** The one line you get without unfolding: what changed. */
export const summariseNeedsSync = ({
  cubeDataHasChanged,
  presentationHasChanged
}: ShouldSyncQuoteReasons): string => {
  const changed = [
    cubeDataHasChanged ? 'data' : undefined,
    presentationHasChanged ? 'presentation' : undefined
  ].filter(Boolean)

  if (!changed.length) {
    return 'Needs sync'
  }

  return `Needs sync - ${changed.join(' and ')}`
}

/**
 * A published quote is saved with a reload, because the save drops it back to
 * draft and the editor has to be told what the server made of that.
 */
export const summariseSaveTriggered = ({
  reload
}: {
  reload: boolean
}): string => (reload ? 'Save triggered - reloading' : 'Save triggered')

export const summariseSyncComplete = ({
  elapsedMs
}: {
  elapsedMs: number
}): string => `Sync complete - ${elapsedMs}ms`
